import type { PortalRequestItemKind } from "./portal-requests.js"

/* The shapes a client's answer takes on the kinds that hold more than one
   word: an amount, a table, a checklist and a choice that takes several
   answers. Plain logic, shared by the API that checks an answer and the
   screens that build one. */

/** A table answer: one row per line the client added, one cell per column
 * the firm named. */
export type PortalTableRows = readonly (readonly string[])[]

/** What an answer holds. Text, a date and an amount are strings, yes or no
 * is a boolean, the picked choices and the ticked to-dos are strings, and a
 * table is its rows. */
export type PortalAnswerValue =
  | string
  | boolean
  | readonly string[]
  | PortalTableRows

/** What a draft holds: the text being typed, or a table's rows. */
export type PortalDraftValue = string | PortalTableRows

/** The kinds whose `options` hold a list the firm wrote: the choices, the
 * table's columns or the to-dos. */
export const portalItemListLimits = {
  choice: { min: 2, max: 10 },
  table: { min: 1, max: 6 },
  checklist: { min: 1, max: 20 },
} as const satisfies Partial<
  Record<PortalRequestItemKind, { min: number; max: number }>
>

export type PortalListItemKind = keyof typeof portalItemListLimits

export const portalItemListMax = Math.max(
  ...Object.values(portalItemListLimits).map((limit) => limit.max)
)

export const portalTableMaxRows = 50
export const portalTableCellMaxLength = 500

export const isPortalListItemKind = (
  kind: PortalRequestItemKind
): kind is PortalListItemKind => kind in portalItemListLimits

/** Why an item's list does not fit its kind, or null when it does. */
export const portalItemListProblem = (
  kind: PortalRequestItemKind,
  options: readonly string[]
): string | null => {
  if (!isPortalListItemKind(kind))
    return options.length > 0
      ? "Only a choice, table or checklist item can have a list"
      : null
  const { min, max } = portalItemListLimits[kind]
  if (options.length < min || options.length > max)
    return kind === "choice"
      ? `Add ${min} to ${max} choices`
      : kind === "table"
        ? `Add ${min} to ${max} columns`
        : `Add ${min} to ${max} to-dos`
  if (new Set(options).size !== options.length)
    return "Give each entry a different name"
  return null
}

/** The currency an amount is asked in. Firms have no currency setting yet,
 * so every amount is in US dollars until they do. */
export const portalAmountCurrency = "USD"

const amountPattern = /^-?\d{1,13}(?:\.\d{1,2})?$/

/** Whether a string is an amount as the answer keeps it: digits, an
 * optional minus and up to two decimals, with no symbol or separator. */
export const isPortalAmount = (value: string): boolean =>
  amountPattern.test(value)

/** The amount the client typed as the answer keeps it, always with its
 * cents: "$1,250.5" is "1250.50" and "12" is "12.00". Null when it is not
 * an amount. */
export const parsePortalAmount = (input: string): string | null => {
  const cleaned = input
    .trim()
    .replace(/[\s,]/g, "")
    .replace(/^(-?)\$/, "$1")
  if (!isPortalAmount(cleaned)) return null
  const [whole, cents = ""] = cleaned.split(".")
  return `${whole}.${cents.padEnd(2, "0")}`
}

/** An amount in the currency it was asked in, e.g. "$1,250.50". */
export const formatPortalAmount = (
  value: string,
  currency: string = portalAmountCurrency
): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    Number(value)
  )

const isStringList = (value: PortalAnswerValue): value is readonly string[] =>
  Array.isArray(value) &&
  value.every((entry: unknown) => typeof entry === "string")

/** Picks from the item's list: at least one, each on the list, none twice. */
export const isPortalPickList = (
  value: PortalAnswerValue,
  options: readonly string[]
): value is readonly string[] =>
  isStringList(value) &&
  value.length > 0 &&
  new Set(value).size === value.length &&
  value.every((entry) => options.includes(entry))

/** A table the client filled in: 1 to 50 rows, one cell per column, and no
 * row left blank. */
export const isPortalTableRows = (
  value: PortalAnswerValue,
  columns: readonly string[]
): value is PortalTableRows =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.length <= portalTableMaxRows &&
  value.every(
    (row: unknown) =>
      Array.isArray(row) &&
      row.length === columns.length &&
      row.every(
        (cell: unknown) =>
          typeof cell === "string" && cell.length <= portalTableCellMaxLength
      ) &&
      row.some((cell: string) => cell.trim().length > 0)
  )

/** The rows worth sending: a row with every cell blank is dropped. */
export const filledPortalTableRows = (rows: PortalTableRows): string[][] =>
  rows
    .map((row) => row.map((cell) => cell.trim()))
    .filter((row) => row.some(Boolean))

/** Whether a checklist answer ticks every to-do. The ticks before that are
 * progress: the item is still the client's to finish. */
export const isPortalChecklistFinished = (
  value: PortalAnswerValue,
  options: readonly string[]
): boolean =>
  isStringList(value) && options.every((option) => value.includes(option))

/** "2 of 3 done" for a checklist: the ticks that are still on the list, of
 * the to-dos the list has now. A tick on a to-do the firm took off is not
 * counted. */
export const portalChecklistProgress = (
  options: readonly string[],
  value: PortalAnswerValue | undefined
): string => {
  const ticked =
    value !== undefined && isStringList(value)
      ? options.filter((option) => value.includes(option)).length
      : 0
  return `${ticked} of ${options.length} done`
}

/** A draft fits its item: a table keeps rows, every other kind text. */
export const portalDraftFits = (
  kind: PortalRequestItemKind,
  value: PortalDraftValue
): boolean => (kind === "table") === Array.isArray(value)

/** An answer on one line, as the firm reads it in a list: "Yes", "$1,250.00",
 * "Payroll, Rent", "2 of 3 done" or "4 rows". */
export const portalAnswerText = (
  item: { kind: PortalRequestItemKind; options: readonly string[] },
  answer: { value: PortalAnswerValue; currency?: string }
): string => {
  const { value } = answer
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (item.kind === "amount" && typeof value === "string")
    return isPortalAmount(value)
      ? formatPortalAmount(value, answer.currency)
      : value
  if (item.kind === "table")
    return `${value.length} ${value.length === 1 ? "row" : "rows"}`
  if (item.kind === "checklist")
    return portalChecklistProgress(item.options, value)
  return typeof value === "string" ? value : value.join(", ")
}
